import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/uiDir/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/uiDir/card";
import { DashboardStats } from "@/components/dashboard/DashboardStats";
import { supabase } from "@/lib/supabaseClient";
import { toast } from "sonner";

interface ProfileStats {
  profile_views: number;
  total_likes: number;
  followers_count: number;
  projects_count: number;
}

const Analytics = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<ProfileStats>({
    profile_views: 0,
    total_likes: 0,
    followers_count: 0,
    projects_count: 0,
  });

  useEffect(() => {
    const loadStats = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate("/login");
          return;
        }

        const { data, error } = await supabase
          .from("profiles")
          .select("profile_views, total_likes, followers_count, projects_count")
          .eq("id", user.id)
          .single();

        if (error) throw error;
        if (data) {
          setStats({
            profile_views: data.profile_views || 0,
            total_likes: data.total_likes || 0,
            followers_count: data.followers_count || 0,
            projects_count: data.projects_count || 0,
          });
        }
      } catch (err) {
        console.error("Error loading analytics:", err);
        toast.error("Failed to load analytics");
      } finally {
        setIsLoading(false);
      }
    };

    loadStats();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10 max-w-6xl">
        <div className="mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
          </Link>
          <h1 className="text-4xl font-heading font-bold mb-2">Analytics</h1>
          <p className="text-muted-foreground">Your latest portfolio insights</p>
        </div>

        <div className="space-y-6">
          <DashboardStats
            profileViews={stats.profile_views}
            totalLikes={stats.total_likes}
            followersCount={stats.followers_count}
            projectsCount={stats.projects_count}
          />

          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-lg">Insights</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-2">
              <p>Track how your projects perform and keep sharing your best work.</p>
              <p>Engage with peers to grow your reach and portfolio visibility.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
